import { gql } from "@apollo/client";
import Input from "components/common/form/inputs/Input";
import Textarea from "components/common/form/inputs/Textarea";
import DateInput from "components/common/form/inputs/date/DateInput";
import PageForm, { PageFormProps } from "components/common/page/PageForm";
import AccommodationWorkMaintenanceLogFormFields from "components/entities/accommodation/work/maintenance/AccommodationWorkMaintenanceLogFormFields";
import SeasonDropdown from "components/entities/app/season/inputs/SeasonDropdown";
import WorkersInput, { WorkersFormType } from "components/entities/app/work/WorkersInput";
import { uuid } from "config/app-constants";
import { AppRoutes } from "config/router";
import { useState } from "react";
import { IdName } from "types/common-types";
import { AccommodationMaintenanceFormQuery, AccommodationWorkMaintenanceInput, PaidTypes, SaveAccommodationMaintenanceFormMutation, SaveAccommodationMaintenanceFormMutationVariables } from "types/graphql";
import { PersonType } from "types/person-types";
import { SeasonType } from "types/season-type";
import { DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";

type Query = AccommodationMaintenanceFormQuery;
type Mutation = SaveAccommodationMaintenanceFormMutation;
type MutationVariables = SaveAccommodationMaintenanceFormMutationVariables;

const query = gql`
query AccommodationMaintenanceForm($id: Int!) {
    season(where: {active: {eq: true}}) {
      id
      name
      active
    }
    accommodationWorkMaintenance(id: $id) {
      id
      workers {
        items {
          person {
            id
            name
          }
          price
          id
        }
      }
      season {
        active
        id
        name
      }
      logs {
        items {
            id
          maintenanceType {
            name
            id
          }
          investment {
            id
            name
            investmentType
            unit
            unitPrice
            total
          }
          logRooms {
            items {
              id
              room {
                id
                roomType {
                  name
                  id
                  color
                }
                name
              }
            }
          }
        }
      }
      startDate
      endDate
      note
      name
    }
  }
`;

const mutation = gql`
mutation SaveAccommodationMaintenanceForm($model: AccommodationWorkMaintenanceInput!) {
    accommodationWorkMaintenance(model: $model) {
      id
    }
}
`;

type Model = {
    season?: SeasonType,
    endDate?: Date,
    startDate: Date,
    id: number|string,
    name: string,
    note?: string,
    workers: WorkersFormType[],
    logs: {
        id: string | number,
        investment?: IdName,
        maintenanceType?: IdName,
        rooms: {
            id: string | number,
            roomId: number,
            roomName: string,
        }[]
    }[]
}

const AccommodationMaintenanceFormPage = () => {
    const context = useAppContext();
    const [model, setModel] = useState<Model>({
        season: undefined,
        endDate: undefined,
        startDate: new Date(),
        id: 0,
        name: '',
        note: '',
        workers: [{
            person: undefined,
            price: '',
            id: uuid()
        }],
        logs: [{
            id: uuid(),
            investment: undefined,
            maintenanceType: undefined,
            rooms: []
        }]
    })

    const pageConfig: PageFormProps<Query, Mutation, MutationVariables> = {
        title: "pages.accommodation.work.maintenance.form",
        name: "AccommodationMaintenanceFormPage",
        load: {
            resolveQueyData(data) {
                let theModel: Model = {
                    ...model,
                    season: data?.season || undefined,
                }
    
                if(data?.accommodationWorkMaintenance) {
                    theModel = {
                        endDate: data.accommodationWorkMaintenance.endDate ? new Date(data.accommodationWorkMaintenance.endDate) : undefined,
                        startDate: new Date(data.accommodationWorkMaintenance.startDate),
                        id: data.accommodationWorkMaintenance.id,
                        name: data.accommodationWorkMaintenance.name,
                        note: data.accommodationWorkMaintenance.note || '',
                        season: data.accommodationWorkMaintenance.season as SeasonType,
                        workers: data.accommodationWorkMaintenance.workers.items.map(w => ({
                            person: w.person as PersonType,
                            price: w.price,
                            id: w.id
                        })),
                        logs: data.accommodationWorkMaintenance.logs.items.map(l => {
                            return {
                                id: l.id,
                                maintenanceType: l.maintenanceType,
                                investment: (l.investment || undefined) as any,
                                rooms: l.logRooms.items.map(f => {
                                    return {
                                        id: f.id,
                                        roomId: f.room.id,
                                        roomName: f.room.name
                                    }
                                })
                            }
                        })
                    }
                }
    
                setModel(theModel)
            },
            query: query,
        },
        onSubmit: {
            mutation,
            resolveMutationData() {
                const inputModel: AccommodationWorkMaintenanceInput = {
                    id: Number(model.id) || 0,
                    name: model.name!,
                    startDate: model.startDate && DateUtil.toIsoDate(model.startDate),
                    endDate: model.endDate && DateUtil.toIsoDate(model.endDate),
                    note: model.note,
                    seasonId: model.season!.id,
                    workers: model.workers!.map(w => ({
                        personId: Number(w.person!.id),
                        price: Number(w.price),
                        id: Number(w.id) || 0,
                        date: DateUtil.toIsoDate(model.startDate)
                    })),
                    logs: model.logs!.map(l => ({
                        id: Number(l.id) || 0,
                        workId: Number(model.id) || 0,
                        paidType: PaidTypes.Total,
                        investmentId: Number(l.investment?.id),
                        maintenanceTypeId: Number(l.maintenanceType?.id),
                        logRooms: l.rooms.map(r => ({
                            id: Number(r.id) || 0,
                            roomId: r.roomId,
                            logId: Number(l.id) || 0
                        }))
                    }))
                }
    
                return {
                    model: inputModel
                };
            },
            onSuccess(data) {
                if(data?.accommodationWorkMaintenance?.id) {
                    context.navigate(AppRoutes.ACCOMMODATION_MAINTENANCE_VIEW, {id: data.accommodationWorkMaintenance.id}, {
                        replace: true
                    })
                }
            }
        }
    }

    const insertIntoModel = (key: keyof Model, value: any) => {
        setModel({
            ...model,
            [key]: value
        });
    };

    return (
        <PageForm {...pageConfig} >
            <fieldset>
                    <SeasonDropdown
                        value={model.season}
                        required
                        label="fields.season.name"
                        onChange={s => insertIntoModel('season', s)}
                    />
                    <Input
                        name="name"
                        value={model.name}
                        onChange={v => insertIntoModel('name', v)}
                        required
                        maxLength={128}
                        minLength={2}
                        label="fields.name.name"
                    />
                    <DateInput
                        required
                        label="fields.startDate.name"
                        value={model.startDate}
                        name="startDate"
                        onChange={d => insertIntoModel('startDate', d)}
                    />
                    <DateInput
                        label="fields.endDate.name"
                        value={model.endDate}
                        min={model.startDate}
                        disabled={!model.startDate}
                        name="startDate"
                        onChange={d => insertIntoModel('endDate', d)}
                    />
                </fieldset>
                <fieldset>
                    <AccommodationWorkMaintenanceLogFormFields
                        name="agricultureWorkMaintenanceFormPageLogs"
                        value={model.logs}
                        required
                        onChange={v => insertIntoModel('logs', v)}
                    />
                </fieldset>
                <fieldset>
                    <WorkersInput
                        name="agricultureWorkMaintenanceFormPageWorkers"
                        value={model.workers}
                        required
                        onChange={v => insertIntoModel('workers', v)}
                    />
                </fieldset>
                <fieldset>
                    <Textarea label="fields.note.name" name="note" value={model.note} onChange={v => insertIntoModel('note', v)}/>
                </fieldset>
        </PageForm>
    );
};

export default AccommodationMaintenanceFormPage;
